import { supabase } from '../lib/supabase';

/**
 * Fetch all franchisee users (Super Admin only)
 */
export const fetchFranchisees = async () => {
  // Use SECURITY DEFINER RPC to bypass potential RLS issues on user_profiles
  const { data, error } = await supabase?.rpc('get_all_franchisees');
  if (!error) {
    return data || [];
  }

  console.warn('[fetchFranchisees] RPC failed, trying direct query fallback:', error?.message);

  // Fallback: direct query (works if RLS is correctly configured)
  const { data: fallbackData, error: fallbackError } = await supabase
    ?.from('user_profiles')
    ?.select('id, email, full_name, role, created_at')
    ?.eq('role', 'franchisee')
    ?.order('created_at', { ascending: false });

  if (fallbackError) {
    console.error('[fetchFranchisees] Fallback also failed:', fallbackError?.message);
    throw new Error(`Impossible de charger les franchisés: ${fallbackError?.message}`);
  }

  if (!fallbackData?.length) {
    // RLS likely blocked the query silently — surface the original RPC error
    console.error('[fetchFranchisees] Fallback returned empty — RLS likely blocked the query. Original RPC error:', error?.message);
    throw new Error(`Impossible de charger les franchisés (RLS): ${error?.message}`);
  }

  return fallbackData;
};

/**
 * Fetch restaurant assignments for a specific franchisee (Super Admin only)
 */
export const fetchFranchiseeAssignments = async (franchiseeId) => {
  const { data, error } = await supabase
    ?.from('franchisee_restaurant_access')
    ?.select('id, restaurant_id, granted_at, restaurants(id, slug, display_name, status)')
    ?.eq('franchisee_id', franchiseeId)
    ?.order('granted_at', { ascending: false });
  if (error) throw error;
  return data || [];
};

/**
 * Fetch all assignments with restaurant info (Super Admin only)
 */
export const fetchAllFranchiseeAssignments = async () => {
  const { data, error } = await supabase
    ?.from('franchisee_restaurant_access')
    ?.select('id, franchisee_id, restaurant_id, granted_at, user_profiles(id, email, full_name), restaurants(id, slug, display_name, status)');
  if (error) throw error;
  return data || [];
};

/**
 * Assign a restaurant to a franchisee (Super Admin only)
 */
export const assignRestaurantToFranchisee = async (franchiseeId, restaurantId, grantedBy) => {
  const { data, error } = await supabase
    ?.from('franchisee_restaurant_access')
    ?.insert({ franchisee_id: franchiseeId, restaurant_id: restaurantId, granted_by: grantedBy })
    ?.select()
    ?.single();
  if (error) throw error;
  return data;
};

/**
 * Revoke a restaurant from a franchisee (Super Admin only)
 */
export const revokeRestaurantFromFranchisee = async (franchiseeId, restaurantId) => {
  const { error } = await supabase
    ?.from('franchisee_restaurant_access')
    ?.delete()
    ?.eq('franchisee_id', franchiseeId)
    ?.eq('restaurant_id', restaurantId);
  if (error) throw error;
};

/**
 * Create a new franchisee user account (Super Admin only)
 * Uses invite flow — sends invite email to the franchisee
 */
export const inviteFranchisee = async (email, fullName) => {
  const { data, error } = await supabase?.functions?.invoke('invite-restaurant-member', {
    body: {
      email,
      role: 'franchisee',
      full_name: fullName,
    },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
};

/**
 * Update franchisee profile (name, etc.)
 */
export const updateFranchiseeProfile = async (franchiseeId, updates) => {
  const { data, error } = await supabase
    ?.from('user_profiles')
    ?.update({ ...updates, updated_at: new Date()?.toISOString() })
    ?.eq('id', franchiseeId)
    ?.eq('role', 'franchisee')
    ?.select()
    ?.single();
  if (error) throw error;
  return data;
};

/**
 * Get restaurants accessible by the current franchisee (Franchisee only)
 */
export const fetchMyFranchiseeRestaurants = async () => {
  const { data, error } = await supabase?.rpc('get_franchisee_restaurants');
  if (error) throw error;
  return data || [];
};

/**
 * Bulk update restaurant profiles across all franchisee-owned restaurants
 * @param {string[]} restaurantIds - Array of restaurant IDs to update
 * @param {object} profileUpdates - Fields to update in restaurant_profile
 */
export const bulkUpdateRestaurantProfiles = async (restaurantIds, profileUpdates) => {
  if (!restaurantIds?.length) return [];
  const results = await Promise.allSettled(
    restaurantIds?.map((restaurantId) =>
      supabase
        ?.from('restaurant_profile')
        ?.update({ ...profileUpdates, updated_at: new Date()?.toISOString() })
        ?.eq('restaurant_id', restaurantId)
    )
  );
  const errors = results
    ?.filter((r) => r?.status === 'rejected' || r?.value?.error)
    ?.map((r) => r?.reason?.message || r?.value?.error?.message || 'Unknown error');
  if (errors?.length) throw new Error(`Bulk update failed for ${errors?.length} restaurant(s): ${errors?.[0]}`);
  return results;
};

/**
 * Bulk insert a dish into multiple restaurants
 * @param {string[]} restaurantIds - Array of restaurant IDs
 * @param {object} dishData - Dish fields (without restaurant_id)
 */
export const bulkAddDishToRestaurants = async (restaurantIds, dishData) => {
  if (!restaurantIds?.length) return [];
  const dishesToInsert = restaurantIds?.map((restaurantId) => ({
    ...dishData,
    restaurant_id: restaurantId,
    created_at: new Date()?.toISOString(),
    updated_at: new Date()?.toISOString(),
  }));
  const { data, error } = await supabase
    ?.from('menu_dishes')
    ?.insert(dishesToInsert)
    ?.select();
  if (error) throw error;
  return data || [];
};

/**
 * Bulk add a promotion to multiple restaurants
 * @param {string[]} restaurantIds - Array of restaurant IDs
 * @param {object} promotionData - Promotion fields (without restaurant_id)
 */
export const bulkAddPromotionToRestaurants = async (restaurantIds, promotionData) => {
  if (!restaurantIds?.length) return [];
  const promoToInsert = restaurantIds?.map((restaurantId) => ({
    ...promotionData,
    restaurant_id: restaurantId,
    created_at: new Date()?.toISOString(),
    updated_at: new Date()?.toISOString(),
  }));
  const { data, error } = await supabase
    ?.from('promotions')
    ?.insert(promoToInsert)
    ?.select();
  if (error) throw error;
  return data || [];
};
